import { useEffect, useMemo, useRef, useState } from 'react'

import type { MoneyAmount, Relationship } from 'types'
import { usePostMarketplacePurchases } from 'utils/web_api'
import { useWindowLocation } from 'utils/windowLocation'

import { OrderSummary } from './OrderSummary'
import { PurchaserSelection } from './PurchaserSelection'
import { SessionContext } from '../shared'
import { Header } from '../shared/Header'
import type { MarketplaceProduct } from '../shared/types'

export type CoveredParticipant = {
  id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  email: string | null;
  relationship: Relationship;
}

type MarketplaceCheckoutProps = {
  balance: MoneyAmount;
  product: MarketplaceProduct;
  member_id?: string | null;
  member_first_name?: string | null;
  member_last_name?: string | null;
  member_date_of_birth?: string | null;
  member_email?: string | null;
  employer_id?: string | null;
  variant_id?: string | null;
  covered_participants?: CoveredParticipant[];
}

export type PurchaserType = 'myself' | 'spouse' | 'child' | 'other_dependent'

export type OtherDependentInfo = {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  email: string;
}

export default function MarketplaceCheckout ({
  balance,
  product,
  member_id: memberId,
  member_first_name: memberFirstName,
  member_last_name: memberLastName,
  member_date_of_birth: memberDateOfBirth,
  member_email: memberEmail,
  employer_id: employerId,
  variant_id: variantId,
  covered_participants: coveredParticipants = [],
}: MarketplaceCheckoutProps) {
  const formRef = useRef<HTMLFormElement>(null)
  const [purchaserType, setPurchaserType] = useState<PurchaserType>('myself')
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null)
  const [otherDependentInfo, setOtherDependentInfo] = useState<Partial<OtherDependentInfo>>({})
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [formValidity, setFormValidity] = useState(true)

  const purchaseMutation = usePostMarketplacePurchases()
  const { assign } = useWindowLocation()

  const sessionContext = useMemo(() => ({
    id: memberId ?? null,
    employerId: employerId ?? null,
    balance: balance ?? undefined,
    isAuthenticated: true,
    publicView: false,
    publicReferralUrl: '',
  }), [balance, employerId, memberId])

  // Select the variant based on variant_id from URL, or default to first variant
  const selectedVariant = useMemo(() => {
    if (variantId) {
      const variant = product.marketplace_product_variants.find(v => v.id === variantId)
      if (variant) return variant
    }
  }, [product.marketplace_product_variants, variantId])

  useEffect(() => {
    if (purchaserType === 'myself' || purchaserType === 'spouse' || purchaserType === 'child') {
      setFormValidity(true)
    } else if (purchaserType === 'other_dependent') {
      const hasFirstName = !!otherDependentInfo.first_name?.trim()
      const hasLastName = !!otherDependentInfo.last_name?.trim()
      const hasDateOfBirth = !!otherDependentInfo.date_of_birth?.trim()
      const hasEmail = !!otherDependentInfo.email?.trim()

      const isEmailValid = hasEmail && isValidEmail(otherDependentInfo.email!)

      const isValid = hasFirstName && hasLastName && hasDateOfBirth && isEmailValid
      setFormValidity(isValid)
    }
  }, [purchaserType, otherDependentInfo])

  const handleBuyNow = () => {
    setHasAttemptedSubmit(true)

    if (!formValidity || !selectedVariant) {
      return
    }

    // Map purchaser information to purchased_for fields
    let purchasedForData: {
      purchased_for_first_name?: string;
      purchased_for_last_name?: string;
      purchased_for_dob?: string;
      purchased_for_email?: string;
    } = {}

    if (purchaserType === 'myself') {
      // Use the member's own information
      purchasedForData = {
        purchased_for_first_name: memberFirstName || undefined,
        purchased_for_last_name: memberLastName || undefined,
        purchased_for_dob: memberDateOfBirth || undefined,
        purchased_for_email: memberEmail || undefined,
      }
    } else if (purchaserType === 'other_dependent') {
      // Use the manually entered data for other dependent
      purchasedForData = {
        purchased_for_first_name: otherDependentInfo.first_name,
        purchased_for_last_name: otherDependentInfo.last_name,
        purchased_for_dob: otherDependentInfo.date_of_birth,
        purchased_for_email: otherDependentInfo.email,
      }
    } else if (selectedParticipantId) {
      // Find the selected covered participant and map their data
      const participant = coveredParticipants.find(p => p.id === selectedParticipantId)
      if (participant) {
        purchasedForData = {
          purchased_for_first_name: participant.first_name,
          purchased_for_last_name: participant.last_name,
          purchased_for_dob: participant.date_of_birth || undefined,
          purchased_for_email: participant.email || undefined,
        }
      }
    }

    purchaseMutation.mutate(
      {
        data: {
          marketplace_product_variant_id: selectedVariant.id,
          ...purchasedForData,
        },
      },
      {
        onSuccess: (response: any) => {
          // Redirect to order confirmation page with purchase ID
          const purchaseId = response?.id
          assign(`/members/marketplace/orders/${purchaseId}/confirmation`)
        },
        onError: (error: any) => {
          // TODO: Add error state for purchase error (maybe toast?)
          console.error('Purchase error:', error)
        },
      }
    )
  }

  if (!selectedVariant) {
    // TODO: Add error state for malformed variant_id
    return <div>No product with this selected variant</div>
  }

  return (
    <SessionContext.Provider value={sessionContext}>
      <div className='flex fixed inset-0 left-0 md:left-60 overflow-x-auto overflow-y-hidden'>
        {/* Main content area */}
        <main className='overflow-y-auto flex-1'>
          <div className='h-full px-4 md:px-20 pt-6 md:pt-10 pb-20 md:pb-24'>
            <Header product={product} />

            <div className='flex flex-col gap-10 mt-10 max-w-[560px] w-full'>
              <h1 className='heading-lg'>Confirm and checkout</h1>
              <PurchaserSelection
                ref={formRef}
                purchaserType={purchaserType}
                onPurchaserTypeChange={setPurchaserType}
                selectedParticipantId={selectedParticipantId}
                onSelectedParticipantIdChange={setSelectedParticipantId}
                otherDependentInfo={otherDependentInfo}
                onOtherDependentInfoChange={setOtherDependentInfo}
                coveredParticipants={coveredParticipants}
                showValidation={hasAttemptedSubmit}
              />
            </div>
          </div>
        </main>

        {/* Right sidebar */}
        <div className='overflow-y-auto bg-gray-50 shadow-lg relative z-10 w-[480px] flex-shrink-0'>
          <OrderSummary
            balance={balance}
            product={product}
            selectedVariant={selectedVariant}
            onBuyNow={handleBuyNow}
            isProcessing={purchaseMutation.isPending}
            isDisabled={!formValidity}
          />
        </div>
      </div>
    </SessionContext.Provider>
  )
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}
